import { useEffect, useRef, useState } from 'react';

export default function FAQSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const faqs = [
    {
      question: 'タイピングすらできませんが大丈夫？',
      answer: 'もちろんです！電源の入れ方から丁寧に教えます。非エンジニアの父が作ったカリキュラムなので、本当に基礎の基礎から学べます。お子様のペースに合わせて、焦らず進めていきますのでご安心ください。',
      icon: '⌨️'
    },
    {
      question: '親が教えてあげられないのですが…',
      answer: 'だからこそ、当スクールがあります！開発者自身が非エンジニアなので、「教えられない親の気持ち」が誰よりも分かります。お子様だけでなく、保護者様にも分かりやすく説明しますので、一緒に成長を見守りましょう。',
      icon: '👨‍👧'
    },
    {
      question: 'パソコンを持っていないのですが…',
      answer: 'PCレンタルを無料で提供していますので、初期費用を抑えて始められます！まずは体験してみて、お子様が続けたいと思ったら購入を検討していただければ大丈夫です。',
      icon: '💻'
    },
    {
      question: '何歳から始められますか？',
      answer: '6歳から12歳のお子様を対象としています。マウス操作ができれば大丈夫です！年齢やスキルに応じて、最適なカリキュラムをご提案させていただきます。',
      icon: '👦'
    },
    {
      question: 'オンラインと対面、どちらがいいですか？',
      answer: '最初は梅田校での対面授業をおすすめしています。お子様が慣れてきたら、オンラインに切り替えることも可能です。ご家庭の状況に合わせて柔軟に対応いたします。',
      icon: '🏫'
    },
    {
      question: '途中で辞めることはできますか？',
      answer: 'はい、いつでも退会可能です。違約金や解約金は一切かかりません。お子様のペースや興味に合わせて、無理なく続けていただければと思います。',
      icon: '📝'
    }
  ];

  return (
    <section ref={sectionRef} id="faq" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-pink-50 to-purple-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-yellow-200 rounded-full opacity-20 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-200 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block bg-purple-100 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6">
            <span className="text-purple-600 font-bold text-xs sm:text-sm">よくある質問</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-4 sm:mb-6 px-2">
            <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              不安なこと、何でも聞いてください
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 px-2">保護者様からよくいただく質問をまとめました</p>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-xl ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between text-left hover:bg-pink-50 transition-colors duration-300 cursor-pointer"
              >
                <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                    {faq.icon}
                  </div>
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 pr-2">{faq.question}</h4>
                </div>
                <div className={`w-7 sm:w-8 h-7 sm:h-8 flex items-center justify-center bg-pink-100 rounded-full flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <span className="text-pink-500 font-bold text-lg sm:text-xl">↓</span>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`}
              >
                <div className="px-4 sm:px-8 pb-4 sm:pb-6 pl-14 sm:pl-20">
                  <p className="text-gray-700 text-sm sm:text-lg leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Final Message */}
        <div className={`mt-12 sm:mt-16 bg-gradient-to-br from-yellow-100 to-pink-100 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12 shadow-2xl text-center transform transition-all duration-1000 delay-700 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <div className="text-4xl sm:text-5xl mb-4 sm:mb-6">💌</div>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-800 mb-4 sm:mb-6 px-2">追伸</h3>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8 px-2">
            プログラミングは手段です。<br />
            本当に身につけてほしいのは、<strong className="text-pink-600">自分で考えて形にする力</strong>。<br />
            <br />
            かつて私の娘が目を輝かせたあの瞬間を、<br />
            あなたのお子様にも届けたいです。
          </p>
          <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-white px-4 sm:px-8 py-3 sm:py-4 rounded-full shadow-lg">
            <span className="text-2xl sm:text-3xl">❤️</span>
            <span className="text-base sm:text-lg font-bold text-gray-800">パパより、愛を込めて</span>
            <span className="text-2xl sm:text-3xl">❤️</span>
          </div>
        </div>

        {/* Final CTA */}
        <div className={`mt-8 sm:mt-12 text-center transform transition-all duration-1000 delay-900 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <a 
            href="#trial"
            className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 sm:px-12 py-4 sm:py-6 rounded-full font-black text-lg sm:text-2xl shadow-2xl hover:shadow-pink-300 transform hover:scale-110 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center space-x-2 sm:space-x-3">
              <span>🎉</span>
              <span>無料体験に申し込む</span>
              <span>🎉</span>
            </span>
          </a>
          <p className="mt-4 sm:mt-6 text-gray-600 font-medium text-sm sm:text-base">
            ✅ 1分で完了 ✅ 梅田校・オンラインから選べます
          </p>
        </div>
      </div>
    </section>
  );
}
